// The three assignee surfaces: a task's label, its picker, and the board's
// filter. Pure, because each answers "who is this and can they still be
// picked?" from a different source — the task payload, the active roster, the
// project's own assignee history — and the answers must not drift apart.

import type { Task } from "./api"
import type { RosterPerson } from "./roster"

/** The assignee fields a task carries: enough to label it without a roster. */
type AssigneeFields = Pick<Task, "assigneeId" | "assigneeName" | "assigneeActive">

/** "Unassigned", the name, or "Name (inactive)". */
export function assigneeLabel(task: AssigneeFields): string {
  if (!task.assigneeId) return "Unassigned"
  const name = task.assigneeName ?? "Unknown"
  return task.assigneeActive === false ? `${name} (inactive)` : name
}

export type AssigneePickerItem = {
  id: string | null
  name: string | null
  label: string
  selected: boolean
  /** The current inactive assignee: shown so the task reads correctly, but
   *  not offered — the server would refuse them. */
  disabled: boolean
}

/** The picker's items: Unassigned, the active people, and — when the task's
 *  current assignee is not among them — that person as a selected entry that
 *  cannot be picked. Appended once, and only when missing. */
export function assigneePickerItems(
  assignable: RosterPerson[],
  current: AssigneeFields,
): AssigneePickerItem[] {
  const items: AssigneePickerItem[] = [
    { id: null, name: null, label: "Unassigned", selected: !current.assigneeId, disabled: false },
    ...assignable.map((person) => ({
      id: person.id,
      name: person.name,
      label: person.name,
      selected: person.id === current.assigneeId,
      disabled: false,
    })),
  ]
  if (current.assigneeId && !assignable.some((person) => person.id === current.assigneeId)) {
    items.push({
      id: current.assigneeId,
      name: current.assigneeName,
      label: `${current.assigneeName ?? "Unknown"} (inactive)`,
      selected: true,
      disabled: true,
    })
  }
  return items
}

/** One row of `GET /projects/:id/assignees`. */
export type ProjectAssignee = { id: string; name: string; active: boolean }

export type AssigneeFilterOption = {
  value: string
  label: string
  /** A rule between the active roster and the owners the roster no longer
   *  lists. Set on the first option of the second group. */
  dividerBefore?: boolean
}

/** The board filter's options: Me, the active people, then the people who own
 *  tasks on this board without being in the active roster — an imported
 *  disabled owner, a removed member — labelled "(inactive)". De-duplicated. */
export function assigneeFilterOptions(
  meId: string,
  assignable: RosterPerson[],
  projectAssignees: ProjectAssignee[],
): AssigneeFilterOption[] {
  const options: AssigneeFilterOption[] = [{ value: meId, label: "Me" }]
  const seen = new Set([meId])
  for (const person of assignable) {
    if (seen.has(person.id)) continue
    seen.add(person.id)
    options.push({ value: person.id, label: person.name })
  }
  let firstOwner = true
  for (const owner of projectAssignees) {
    if (seen.has(owner.id)) continue
    seen.add(owner.id)
    const label = owner.active ? owner.name : `${owner.name} (inactive)`
    options.push(firstOwner ? { value: owner.id, label, dividerBefore: true } : { value: owner.id, label })
    firstOwner = false
  }
  return options
}
